/**
 * TodayView - Refactored with ComponentRouter
 *
 * Fully data-driven timeline view that displays today's entities.
 * Uses ComponentRouter to dynamically render any entity type.
 *
 * Architecture:
 * - Fetches entities from multiple stores
 * - Groups by entity type
 * - Dynamic sidebar with entity counts
 * - Uses renderEntityRow() for consistent rendering
 */

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { renderEntityRow } from '../components/common/registry/ComponentRouter';
import { useEntityModal } from '../hooks/useEntityModal';
import { ContactWidget } from '../components/hub/ContactWidget';
import { SupportTicketsWidget } from '../components/dashboard/SupportTicketsWidget';
import { useAppShell } from '../layouts/AppShell';
import { useProjectsStore, useHubStore, useCalendarEvents, useInvoices, useMovements, listUnseenMessages, type ImapMessage } from '@business-assistant/shared';
import { useTranslation } from '../hooks';
import { uniqueById } from '../utils/arrayUtils';
import { isToday as isDemoToday, getToday, DEMO_TODAY } from '@business-assistant/shared-data';
import type { Email } from '../components/rows/EmailRow';
import {
  ListTodo,
  FolderKanban,
  Users,
  Phone,
  Mail,
  MessageCircle,
  FileText,
  Clock,
  Flag,
  Calendar,
  Landmark,
  File,
  AlertCircle,
  TrendingUp,
  TrendingDown,
  LifeBuoy,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// =============================================================================
// TYPES
// =============================================================================

interface EntityGroup {
  type: string; // 'Task', 'Project', 'Contact', etc.
  prefix: string; // 'tsk', 'prj', 'cnt', etc.
  icon: React.ReactNode;
  entities: any[];
  color: string;
}

// =============================================================================
// HELPER FUNCTIONS - DATE FILTERING
// =============================================================================

/**
 * Check if a date is today (uses DEMO_TODAY in demo mode)
 */
function isToday(dateStr: string | undefined): boolean {
  if (!dateStr) return false;
  return isDemoToday(dateStr);
}

/**
 * Check if a date is within the last 24 hours (relative to DEMO_TODAY)
 */
function isLast24Hours(dateStr: string | undefined): boolean {
  if (!dateStr) return false;
  const date = new Date(dateStr);
  const today = new Date(getToday());
  today.setHours(23, 59, 59, 999); // End of demo today
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  return date >= yesterday && date <= today;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getIconForType(prefix: string): React.ReactNode {
  const icons: Record<string, React.ReactNode> = {
    tsk: <ListTodo size={16} />,
    prj: <FolderKanban size={16} />,
    cnt: <Users size={16} />,
    cal: <Phone size={16} />,
    eml: <Mail size={16} />,
    msg: <MessageCircle size={16} />,
    inv: <FileText size={16} />,
    tme: <Clock size={16} />,
    mst: <Flag size={16} />,
    evt: <Calendar size={16} />,
    fil: <File size={16} />,
    mov: <Landmark size={16} />,
    cnv: <MessageCircle size={16} />,
  };
  return icons[prefix] || <ListTodo size={16} />;
}

function getColorForType(prefix: string): string {
  const colors: Record<string, string> = {
    tsk: '#3b82f6',
    prj: '#8b5cf6',
    cnt: '#10b981',
    cal: '#1D5BBF',
    eml: '#FF6B2D',
    msg: '#00C853',
    inv: '#F59E0B',
    tme: '#06b6d4',
    mst: '#ec4899',
    evt: '#7c3aed',
    fil: '#64748b',
    mov: '#0891b2',
    cnv: '#00C853',
  };
  return colors[prefix] || '#6b7280';
}

// =============================================================================
// COMPONENT
// =============================================================================

export function TodayView() {
  const navigate = useNavigate();
  const { activeFilters, setDrawerSectionsOverride, setActiveFilter } = useAppShell();
  const selectedFilter = activeFilters.oggi || 'all';
  const { t } = useTranslation();
  const { openEntity } = useEntityModal();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin' || user?.role === 'owner';

  // State for contact/company profile panel (when clicking avatar)
  const [selectedContact, setSelectedContact] = useState<{ name: string; id?: string } | null>(null);
  // State for unread IMAP emails
  const [unreadImapEmails, setUnreadImapEmails] = useState<Email[]>([]);

  // Load unread emails from IMAP server
  useEffect(() => {
    const loadUnreadEmails = async () => {
      try {
        const messages = await listUnseenMessages('INBOX', 100);
        // Convert IMAP messages to Email format
        const emails: Email[] = messages.map((msg: ImapMessage) => ({
          id: `eml-${msg.uid}`,
          from: msg.from ? (msg.from.name ? `${msg.from.name} <${msg.from.address}>` : msg.from.address) : undefined,
          to: msg.to.length > 0 ? (msg.to[0].name ? `${msg.to[0].name} <${msg.to[0].address}>` : msg.to[0].address) : undefined,
          subject: msg.subject || '(No subject)',
          body: msg.snippet,
          direction: 'in' as const,
          at: msg.date,
          read: false, // These are unread
          hasAttachments: msg.hasAttachments,
        }));
        setUnreadImapEmails(emails);
      } catch {
        // IMAP not connected - silently ignore
      }
    };
    loadUnreadEmails();
  }, []);

  // Get data from stores
  const tasks = useProjectsStore((s) => s.tasks);
  const milestones = useProjectsStore((s) => s.milestones);
  const files = useProjectsStore((s) => s.files);
  const conversations = useHubStore((s) => s.conversations);
  // Prefer react-query (canonical, always fresh); fall back to store cache
  // for the brief window before the QueryClient hydrates on first mount.
  const { data: invoices = [] } = useInvoices();
  const { data: movements = [] } = useMovements();
  const { data: events = [] } = useCalendarEvents();

  // Filter entities for today
  const todayEntities = useMemo(() => {
    const today = getToday();

    // Helper to check if invoice is due soon or overdue
    const isInvoiceDueOrOverdue = (inv: any) => {
      if (!inv.dueAt) return false;
      if (inv.status === 'paid' || inv.status === 'cancelled') return false;
      const dueDate = inv.dueAt.split('T')[0];
      return dueDate <= today;
    };

    // Helper to check if conversation needs response (has unread messages)
    const needsResponse = (conv: any) => {
      return (conv.unread || 0) > 0;
    };

    return {
      // Tasks with due date today or start date today (not completed)
      tasks: tasks.filter((task) =>
        (isToday(task.dueAt) || isToday(task.startAt)) && task.status !== 'done'
      ),

      // Milestones due today (not completed)
      milestones: milestones.filter((m) => isToday(m.dueDate) && !m.completed),

      // Files uploaded today (for supervision)
      files: files.filter((f) => isToday(f.createdAt)),

      // Events for today
      events: events.filter((e) => isToday(e.startAt)),

      // Conversations needing response (awaiting)
      conversationsAwaitingResponse: conversations.filter(needsResponse),

      // Invoices due today or overdue
      invoicesDue: invoices.filter(isInvoiceDueOrOverdue),

      // Bank movements from today (new transactions)
      movements: movements.filter((m) => isToday(m.at)),
    };
  }, [tasks, milestones, files, events, conversations, invoices, movements]);

  // Get projects data for grouping tasks
  const projects = useProjectsStore((s) => s.projects);

  // Group entities by MAIN MODULE with contextual subdivisions
  const moduleGroups = useMemo(() => {
    const groups: any[] = [];

    // =========================================================================
    // PROJECTS (Projects Module)
    // Groups: Tasks, Milestones, Files by project
    // EXCLUDED: TimeEntries (not actionable items)
    // =========================================================================
    const projectsModule: any = {
      moduleName: t('nav.projects').toUpperCase(),
      moduleId: 'projects',
      icon: <FolderKanban size={20} />,
      color: '#8b5cf6',
      subgroups: [],
    };

    // Collect all project-related items grouped by project
    const projectActivities = new Map<string, { tasks: any[]; milestones: any[]; files: any[] }>();

    // Group tasks by project
    todayEntities.tasks.forEach((task) => {
      const projectId = task.projectId;
      if (!projectActivities.has(projectId)) {
        projectActivities.set(projectId, { tasks: [], milestones: [], files: [] });
      }
      projectActivities.get(projectId)!.tasks.push(task);
    });

    // Group milestones by project
    todayEntities.milestones.forEach((milestone) => {
      const projectId = milestone.projectId;
      if (!projectActivities.has(projectId)) {
        projectActivities.set(projectId, { tasks: [], milestones: [], files: [] });
      }
      projectActivities.get(projectId)!.milestones.push(milestone);
    });

    // Group files by project
    todayEntities.files.forEach((file) => {
      const projectId = file.projectId;
      if (!projectActivities.has(projectId)) {
        projectActivities.set(projectId, { tasks: [], milestones: [], files: [] });
      }
      projectActivities.get(projectId)!.files.push(file);
    });

    // Create subgroups per project with all activities
    projectActivities.forEach((activities, projectId) => {
      const project = projects.find((p) => p.id === projectId);
      if (project) {
        // Combine all activities for this project
        const allEntities = [
          ...activities.tasks,
          ...activities.milestones,
          ...activities.files,
        ];

        if (allEntities.length > 0) {
          projectsModule.subgroups.push({
            title: project.title,
            projectId: project.id,
            icon: <FolderKanban size={16} />,
            color: '#8b5cf6',
            entities: allEntities,
            type: 'project-activities',
          });
        }
      }
    });

    if (projectsModule.subgroups.length > 0) {
      groups.push(projectsModule);
    }

    // =========================================================================
    // EVENTS (Calendar Module)
    // Today's events
    // =========================================================================
    if (todayEntities.events.length > 0) {
      const eventsModule: any = {
        moduleName: t('calendar.events').toUpperCase(),
        moduleId: 'calendar',
        icon: <Calendar size={20} />,
        color: '#7c3aed',
        subgroups: [{
          title: t('common.today'),
          icon: <Calendar size={16} />,
          color: '#7c3aed',
          entities: uniqueById(todayEntities.events),
          type: 'events',
        }],
      };
      groups.push(eventsModule);
    }

    // =========================================================================
    // HUB (CRM Module)
    // Chat/Email awaiting response, grouped by channel
    // =========================================================================
    const hubModule: any = {
      moduleName: t('nav.hub').toUpperCase(),
      moduleId: 'hub',
      icon: <Users size={20} />,
      color: '#10b981',
      subgroups: [],
    };

    // Add unread IMAP emails FIRST (most important)
    if (unreadImapEmails.length > 0) {
      hubModule.subgroups.push({
        title: t('today.unreadEmails', { count: unreadImapEmails.length }) || `Unread Emails (${unreadImapEmails.length})`,
        icon: <Mail size={16} />,
        color: '#3b82f6',
        entities: unreadImapEmails,
        type: 'emails',
      });
    }

    // Group conversations awaiting response by channel
    if (todayEntities.conversationsAwaitingResponse.length > 0) {
      const byChannel = new Map<string, any[]>();

      todayEntities.conversationsAwaitingResponse.forEach((conv) => {
        const channel = conv.channel || 'other';
        if (!byChannel.has(channel)) {
          byChannel.set(channel, []);
        }
        byChannel.get(channel)!.push(conv);
      });

      // Channel display config
      const channelConfig: Record<string, { label: string; icon: React.ReactNode; color: string }> = {
        email: { label: t('today.emailAwaiting'), icon: <Mail size={16} />, color: '#FF6B2D' },
        whatsapp: { label: t('today.whatsappAwaiting'), icon: <MessageCircle size={16} />, color: '#25D366' },
        telegram: { label: t('today.telegramAwaiting'), icon: <MessageCircle size={16} />, color: '#0088cc' },
        call: { label: t('today.callsToReturn'), icon: <Phone size={16} />, color: '#1D5BBF' },
        sms: { label: t('today.smsAwaiting'), icon: <MessageCircle size={16} />, color: '#6b7280' },
        other: { label: t('today.otherMessages'), icon: <MessageCircle size={16} />, color: '#00C853' },
      };

      byChannel.forEach((convs, channel) => {
        const config = channelConfig[channel] || channelConfig.other;
        hubModule.subgroups.push({
          title: config.label,
          icon: config.icon,
          color: config.color,
          entities: convs,
          type: 'conversations-awaiting',
        });
      });
    }

    if (hubModule.subgroups.length > 0) {
      groups.push(hubModule);
    }

    // =========================================================================
    // ACCOUNTING (Accounting Module)
    // Invoices due/overdue (issued + received), Bank
    // =========================================================================
    const accountingModule: any = {
      moduleName: t('nav.accounting').toUpperCase(),
      moduleId: 'accounting',
      icon: <FileText size={20} />,
      color: '#F59E0B',
      subgroups: [],
    };

    // Group invoices: issued (to collect) vs received (to pay)
    const invoicesToCollect = todayEntities.invoicesDue.filter(
      (inv) => inv.direction === 'issued'
    );
    const invoicesToPay = todayEntities.invoicesDue.filter(
      (inv) => inv.direction === 'received'
    );

    if (invoicesToCollect.length > 0) {
      // Check if any are overdue
      const hasOverdue = invoicesToCollect.some((inv) => inv.status === 'overdue');
      accountingModule.subgroups.push({
        title: hasOverdue ? t('today.toCollectOverdue') : t('calendar.toCollect'),
        icon: <TrendingUp size={16} />,
        color: hasOverdue ? '#ef4444' : '#eab308', // Yellow for to collect
        entities: invoicesToCollect,
        type: 'invoices-issued',
      });
    }

    if (invoicesToPay.length > 0) {
      const hasOverdue = invoicesToPay.some((inv) => inv.status === 'overdue');
      accountingModule.subgroups.push({
        title: hasOverdue ? t('today.toPayOverdue') : t('calendar.toPay'),
        icon: <TrendingDown size={16} />,
        color: '#ef4444', // Red for to pay
        entities: invoicesToPay,
        type: 'invoices-received',
      });
    }

    // Bank transactions (new movements)
    if (todayEntities.movements.length > 0) {
      accountingModule.subgroups.push({
        title: t('today.bankNewTransactions'),
        icon: <Landmark size={16} />,
        color: '#0891b2',
        entities: uniqueById(todayEntities.movements),
        type: 'bank-movements',
      });
    }

    if (accountingModule.subgroups.length > 0) {
      groups.push(accountingModule);
    }

    return groups;
  }, [todayEntities, projects, t, unreadImapEmails]);

  // Calculate total count
  const totalCount = useMemo(() => {
    return moduleGroups.reduce((sum, module) => {
      const moduleTotal = module.subgroups.reduce((subSum: number, subgroup: any) =>
        subSum + subgroup.entities.length, 0);
      return sum + moduleTotal;
    }, 0);
  }, [moduleGroups]);

  // Set up sidebar with today's filters
  useEffect(() => {
    const filterItems = [
      {
        id: 'all',
        label: t('calendar.eventTypes.all'),
        hint: t('today.hints.all'),
        icon: 'calendar',
        count: totalCount,
        active: selectedFilter === 'all',
        onClick: () => setActiveFilter('oggi', 'all'),
      },
      {
        id: 'tasks',
        label: t('calendar.eventTypes.task'),
        hint: t('today.hints.tasks'),
        icon: 'task',
        count: todayEntities.tasks.length,
        active: selectedFilter === 'tasks',
        onClick: () => setActiveFilter('oggi', 'tasks'),
      },
      {
        id: 'events',
        label: t('calendar.events'),
        hint: t('today.hints.events'),
        icon: 'event',
        count: todayEntities.events.length,
        active: selectedFilter === 'events',
        onClick: () => setActiveFilter('oggi', 'events'),
      },
      {
        id: 'messages',
        label: t('calendar.messages'),
        hint: t('today.hints.messages'),
        icon: 'message',
        count: todayEntities.conversationsAwaitingResponse.length,
        active: selectedFilter === 'messages',
        onClick: () => setActiveFilter('oggi', 'messages'),
      },
      {
        id: 'invoices',
        label: t('accounting.sections.invoices'),
        hint: t('today.hints.invoices'),
        icon: 'wallet',
        count: todayEntities.invoicesDue.length,
        active: selectedFilter === 'invoices',
        onClick: () => setActiveFilter('oggi', 'invoices'),
      },
    ];

    setDrawerSectionsOverride([
      { title: t('common.today'), items: filterItems },
    ]);
    // No cleanup needed - AppShell resets sections on view change
  }, [selectedFilter, totalCount, todayEntities, setActiveFilter, setDrawerSectionsOverride, t]);

  // Handle entity click - navigate to project dashboard or open via URL
  const handleEntityClick = useCallback((entity: any) => {
    console.log('Entity clicked:', entity);

    // Projects: navigate to project dashboard (not edit form)
    if (entity?.id?.startsWith('prj-')) {
      navigate(`/projects/${entity.id}/dashboard`);
      return;
    }

    // Other entities: open via URL (GlobalEntityModal will handle rendering)
    if (entity?.id) {
      openEntity(entity.id);
    }
  }, [navigate, openEntity]);

  // Handle action click
  const handleActionClick = useCallback((actionType: string, entityId: string) => {
    console.log('Action clicked:', actionType, entityId);
    // TODO: Open action form (email, call, task, etc.)
  }, []);

  // Handle avatar click - open contact/company profile
  const handleAvatarClick = useCallback((name: string, id?: string) => {
    console.log('Avatar clicked:', name, id);
    setSelectedContact({ name, id });
  }, []);

  return (
    <>
      <div style={styles.container}>
        {/* Main content */}
        <div style={styles.content}>
          {/* Support Tickets Widget - Shows for admins or users with their own tickets */}
          <div style={{ marginBottom: '1.5rem' }}>
            <SupportTicketsWidget maxItems={5} />
          </div>

          {moduleGroups.length === 0 ? (
            <div style={styles.emptyState}>
              <p>{t('today.noItemsForToday')}</p>
            </div>
          ) : (
            moduleGroups.map((module) => {
              // Calculate total count for this module
              const moduleTotal = module.subgroups.reduce(
                (sum: number, sg: any) => sum + sg.entities.length, 0
              );

              return (
                <section key={module.moduleId} style={styles.moduleSection}>
                  {/* Module header (PROGETTI, HUB, EVENTI, CONTABILITÀ) */}
                  <div style={styles.moduleHeader}>
                    <div style={styles.moduleTitle}>
                      <span style={{ ...styles.moduleIcon, color: module.color }}>
                        {module.icon}
                      </span>
                      <h2 style={{ ...styles.moduleName, color: module.color }}>
                        {module.moduleName}
                      </h2>
                      <span style={styles.moduleCount}>({moduleTotal})</span>
                    </div>
                  </div>

                  {/* Subgroups */}
                  {module.subgroups.map((subgroup: any, idx: number) => (
                    <div key={idx} style={styles.subgroupSection}>
                      {/* Subgroup header */}
                      <div
                        style={styles.subgroupHeader}
                        onClick={() => {
                          if (subgroup.projectId) {
                            console.log('Navigate to project:', subgroup.projectId);
                            // TODO: Navigate to project detail
                          }
                        }}
                      >
                        <div style={styles.subgroupTitle}>
                          <span style={{ ...styles.subgroupIcon, color: subgroup.color }}>
                            {subgroup.icon}
                          </span>
                          <h3 style={styles.subgroupName}>{subgroup.title}</h3>
                          <span style={styles.subgroupCount}>({subgroup.entities.length})</span>
                        </div>
                      </div>

                      {/* Entity rows */}
                      <div style={styles.subgroupContent}>
                        {subgroup.entities.map((entity: any) =>
                          renderEntityRow(entity, {
                            onTitleClick: handleEntityClick,
                            onActionClick: handleActionClick,
                            onAvatarClick: handleAvatarClick,
                          })
                        )}
                      </div>
                    </div>
                  ))}
                </section>
              );
            })
          )}
        </div>
      </div>

      {/* Contact Widget - Opens when clicking on an avatar */}
      {selectedContact && (
        <ContactWidget
          isOpen={true}
          contactId={selectedContact.id}
          onClose={() => setSelectedContact(null)}
          onSave={() => setSelectedContact(null)}
        />
      )}
    </>
  );
}

// =============================================================================
// STYLES
// =============================================================================

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column' as const,
    height: '100%',
    overflow: 'hidden',
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '1.5rem',
  },
  // Module level (PROGETTI, HUB, CONTABILITÀ)
  moduleSection: {
    marginBottom: '3rem',
  },
  moduleHeader: {
    marginBottom: '1rem',
    paddingBottom: '0.75rem',
    borderBottom: '2px solid #e5e7eb',
  },
  moduleTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
  },
  moduleIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  moduleName: {
    fontSize: '1.75rem',
    fontWeight: 700,
    margin: 0,
    letterSpacing: '0.5px',
    textTransform: 'uppercase' as const,
  },
  moduleCount: {
    fontSize: '1rem',
    color: '#9ca3af',
    fontWeight: 500,
  },
  // Subgroup level (project names, "Da emettere", etc.)
  subgroupSection: {
    marginBottom: '1.5rem',
  },
  subgroupHeader: {
    marginBottom: '0.5rem',
    cursor: 'pointer',
    padding: '0.5rem',
    borderRadius: '0.375rem',
    transition: 'background-color 0.2s',
  },
  subgroupTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },
  subgroupIcon: {
    display: 'flex',
    alignItems: 'center',
  },
  subgroupName: {
    fontSize: '1.125rem',
    fontWeight: 600,
    margin: 0,
    color: '#374151',
  },
  subgroupCount: {
    fontSize: '0.875rem',
    color: '#9ca3af',
  },
  subgroupContent: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '0.5rem',
    overflow: 'hidden',
  },
  emptyState: {
    textAlign: 'center' as const,
    padding: '4rem 2rem',
    color: '#9ca3af',
  },
};
